import { Globe, Smartphone, CheckCircle } from 'lucide-react'
import { useDeviceDetect } from '../hooks/useDeviceDetect'

const PWA_URL = 'https://app.nearbies.co.uk'
const IOS_URL = 'https://apps.apple.com/app/nearbies-local-shop-rewards/id6779376733'
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=uk.co.nearbies'

interface Option {
  id: 'pwa' | 'ios' | 'android'
  label: string
  sublabel: string
  description: string
  features: string[]
  cta: string
  href: string
  comingSoon: boolean
  recommended: boolean
}

const DownloadApp = () => {
  const { isIOS, isAndroid, isMobile } = useDeviceDetect()

  const options: Option[] = [
    {
      id: 'pwa',
      label: 'Web App',
      sublabel: 'All devices',
      description: 'Use Nearbies instantly in your browser. No installation needed — just open and go.',
      features: ['Works on any device', 'Always up to date', 'No storage used'],
      cta: 'Open Web App',
      href: PWA_URL,
      comingSoon: false,
      recommended: !isIOS && !isAndroid,
    },
    {
      id: 'ios',
      label: 'App Store',
      sublabel: 'iPhone & iPad',
      description: 'The full native Nearbies experience for iOS with push notifications and offline browsing.',
      features: ['Push notifications', 'Offline browsing', 'Face ID & Touch ID'],
      cta: 'Download on App Store',
      href: IOS_URL,
      comingSoon: false,
      recommended: isIOS,
    },
    {
      id: 'android',
      label: 'Google Play',
      sublabel: 'Android phones & tablets',
      description: 'The full native Nearbies experience for Android with widgets and background sync.',
      features: ['Push notifications', 'Offline browsing', 'Home screen widgets'],
      cta: 'Get it on Google Play',
      href: ANDROID_URL,
      comingSoon: false,
      recommended: isAndroid,
    },
  ]

  // On mobile, sort the recommended option to the top
  const sorted = isMobile
    ? [...options].sort((a, b) => Number(b.recommended) - Number(a.recommended))
    : options

  const iconClass = (id: Option['id']) => {
    if (id === 'pwa') return 'bg-primary-100'
    if (id === 'ios') return 'bg-gray-100'
    return 'bg-green-100'
  }

  const iconColor = (id: Option['id']) => {
    if (id === 'pwa') return 'text-primary-600'
    if (id === 'ios') return 'text-gray-700'
    return 'text-green-600'
  }

  const ctaClass = (option: Option) => {
    if (option.comingSoon) {
      return 'w-full py-3 px-6 rounded-lg font-semibold text-gray-400 bg-gray-100 cursor-not-allowed text-center'
    }
    if (option.recommended) {
      return 'w-full py-3 px-6 rounded-lg font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-md text-center block'
    }
    return 'w-full py-3 px-6 rounded-lg font-semibold text-primary-700 border-2 border-primary-600 hover:bg-primary-50 transition-colors text-center block'
  }

  return (
    <section id="download" className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Get Nearbies on Your Device
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {isMobile
              ? 'Choose the best way to access Nearbies on your device'
              : 'Use Nearbies instantly in your browser, or download the app for the best mobile experience'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {sorted.map((option) => (
            <div
              key={option.id}
              className={`relative bg-white rounded-2xl p-8 flex flex-col ${
                option.recommended
                  ? 'ring-2 ring-primary-600 shadow-xl'
                  : 'border border-gray-200 shadow-md'
              }`}
            >
              {option.recommended && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="bg-primary-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Recommended for your device
                  </span>
                </div>
              )}

              {option.comingSoon && (
                <div className="absolute top-4 right-4">
                  <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full">
                    Coming Soon
                  </span>
                </div>
              )}

              <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-5 ${iconClass(option.id)}`}>
                {option.id === 'pwa'
                  ? <Globe className={`w-7 h-7 ${iconColor(option.id)}`} />
                  : <Smartphone className={`w-7 h-7 ${iconColor(option.id)}`} />
                }
              </div>

              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900">{option.label}</h3>
                <p className="text-sm text-gray-500 mt-0.5">{option.sublabel}</p>
              </div>

              <p className="text-gray-600 leading-relaxed mb-6 flex-1">
                {option.description}
              </p>

              <ul className="space-y-2 mb-8">
                {option.features.map((f) => (
                  <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-primary-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {option.comingSoon ? (
                <span className={ctaClass(option)}>{option.cta}</span>
              ) : (
                <a href={option.href} className={ctaClass(option)}>
                  {option.cta}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default DownloadApp
